import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { toast } from 'sonner';
import { signIn } from '@/lib/auth-client';

const loginSchema = z.object({
  email: z.string().email('Adresse email invalide'),
  password: z.string().min(8, 'Le mot de passe doit contenir au moins 8 caractères'),
});

type LoginFormValues = z.infer<typeof loginSchema>;

export function LoginForm() {
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<'idle' | 'error'>('idle');

  const {
    register,
    handleSubmit,
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormValues) => {
    setIsLoading(true);
    setStatus('idle');

    try {
      const result = await signIn.email({
        email: data.email,
        password: data.password,
      });

      if (result.error) {
        setStatus('error');

        let errorMessage = 'Email ou mot de passe incorrect';
        if (result.error.message?.includes('désactivé') || result.error.message?.includes('disabled')) {
          errorMessage = 'Votre compte a été désactivé';
        } else if (result.error.message?.includes('Invalid origin')) {
          errorMessage = 'Erreur de configuration du serveur. Veuillez contacter un administrateur.';
        } else if (result.error.message && !result.error.message.includes('Invalid credentials')) {
          errorMessage = result.error.message;
        }

        toast.error(errorMessage);
      } else {
        window.location.href = '/dashboard';
      }
    } catch {
      setStatus('error');
      toast.error('Une erreur inattendue est survenue. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  const onError = (errors: any) => {
    const errorMessage = errors.email?.message || errors.password?.message;
    if (errorMessage) {
      toast.error(errorMessage);
    }
  };

  return (
    <>
      <div className="mb-8">
        <h1 className="font-display text-[30px] font-extrabold tracking-[-0.03em] text-ink mb-2">
          Connectez-vous
        </h1>
        <p className="text-ink-muted text-sm">
          Entrez vos identifiants pour accéder à votre espace collaborateur.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit, onError)} className="space-y-5">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-ink mb-1.5">
            Adresse email
          </label>
          <input
            id="email"
            type="email"
            placeholder="votre@email.com"
            disabled={isLoading}
            autoFocus
            className="w-full px-4 py-3.5 bg-white border border-[#D5DCE6] rounded-goutte-sm text-ink focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand transition-colors text-base"
            {...register('email')}
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label htmlFor="password" className="block text-sm font-medium text-ink">
              Mot de passe
            </label>
            <a
              href="/forgot-password"
              className="text-xs text-brand-deep hover:underline"
            >
              Mot de passe oublié ?
            </a>
          </div>
          <input
            id="password"
            type="password"
            disabled={isLoading}
            className="w-full px-4 py-3.5 bg-white border border-[#D5DCE6] rounded-goutte-sm text-ink focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand transition-colors text-base"
            {...register('password')}
          />
        </div>

        {status === 'error' && (
          <p className="text-[#C2410C] text-sm text-center" role="alert">
            Identifiants invalides. Veuillez réessayer.
          </p>
        )}

        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-ink text-white font-semibold py-3.5 px-4 rounded-goutte-sm hover:bg-[#0B2A50] transition-colors text-base cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              Connexion...
            </span>
          ) : (
            'Se connecter'
          )}
        </button>
      </form>
    </>
  );
}
